/**
 * shared/space_stations.ts — CLIENT MIRROR of server/src/lib/space_stations.ts
 * (the server copy is authoritative; keep them byte-identical below the
 * headers). The station REGISTRY (Space Wave 1): which stations exist, where
 * they sit, how close "docked" is; the per-system arrival point; the wormhole
 * throat seat; the range constants the server enforces. Pure.
 */
import { systemSector, type StarSystem } from './galaxy';

export interface StationSpec {
  key: string;            // '<systemId>:st<n>'
  systemId: string;
  name: string;
  pos: [number, number, number];
  radius: number;
  /** Within this distance of `pos` a pilot may dock (radius + 800). */
  dockRange: number;
  /** space campaign K (2026-10-01): the station's ONE role (`STATION_ROLES`) — what a pilot visits it for. */
  role: StationRole;
}

/** The Commons Reach's five stations, in space_sector.ts POI-table order
 *  (verify:space-stations pins the duplication against POIS). */
export const SYS0_STATIONS: readonly StationSpec[] = [
  { key: 'sys0:st0', systemId: 'sys0', name: 'Waystation Aurel', pos: [380_000, 26_000, 140_000], radius: 2_600, dockRange: 3_400, role: 'refinery' },
  { key: 'sys0:st1', systemId: 'sys0', name: 'Nova Aurel Station', pos: [1_050_000, 240_000, -780_000], radius: 2_900, dockRange: 3_700, role: 'yard' },
  { key: 'sys0:st2', systemId: 'sys0', name: 'Aurel Prime', pos: [9_500, 900, 5_200], radius: 5_200, dockRange: 6_000, role: 'military_fort' },
  { key: 'sys0:st3', systemId: 'sys0', name: 'Farlight Terminal', pos: [-800_000, -140_000, -900_000], radius: 2_700, dockRange: 3_500, role: 'research_post' },
  { key: 'sys0:st4', systemId: 'sys0', name: 'Kessway Depot', pos: [520_000, 180_000, 1_250_000], radius: 2_400, dockRange: 3_200, role: 'salvage_yard' },
];
export const ALT_STATION_RADIUS_M = 1_800;
export const STATION_KEY_RE = /^(sys\d{1,3}|w\d{1,20}):st\d{1,3}$/;

// ── STATION ROLES (space campaign K, 2026-10-01; report §8.3) ───────────────────────────────────────────────
// Every registered station carries ONE role — DATA, not a new station: the shared base services (the Exchange, the
// order book, couriers, the refine line, the foundry, repair) stay at all of them; the role changes ONE thing a pilot
// can use (`station_services` reads it behind `station_services_v1`; off, every station behaves as before).
export type StationRole = 'trade_hub' | 'yard' | 'refinery' | 'research_post' | 'frontier_outpost' | 'salvage_yard' | 'military_fort';
export const STATION_ROLE_KEYS: readonly StationRole[] = ['trade_hub', 'yard', 'refinery', 'research_post', 'frontier_outpost', 'salvage_yard', 'military_fort'];
export interface StationRoleDef { label: string; reason: string; difference: string; cue: string }
export const STATION_ROLE_DEFS: Readonly<Record<StationRole, StationRoleDef>> = {
  trade_hub: { label: 'Trade hub', reason: 'sell and buy in bulk', difference: 'The Exchange here is twice as deep: a bulk lot moves the mark half as far.', cue: 'a cargo queue, the customs desk, order terminals' },
  yard: { label: 'Yard', reason: 'hull work', difference: 'Four repair cradles and the yard\'s own labour rate.', cue: 'service cradles with hulls in them, incomplete orders' },
  refinery: { label: 'Refinery', reason: 'refine ore', difference: 'The refine line yields 45 % here (35 % at other stations).', cue: 'ore bins, processing bays, slag handling' },
  research_post: { label: 'Research post', reason: 'survey work', difference: 'A survey desk: funded recovery tenders and the hazard census.', cue: 'a map room, observation windows, expedition logs' },
  frontier_outpost: { label: 'Frontier outpost', reason: 'a refuge at the rim', difference: 'Limited repair from a relief stock: one cradle, the hull to 60 %.', cue: 'sparse shelves, scorched plating, a relief ship due' },
  salvage_yard: { label: 'Salvage yard', reason: 'get your cargo back', difference: 'The wreck berth: the yard\'s tug recovers what YOU own from a wreck in this system.', cue: 'a wreck berth, the provenance desk, an inspection bench' },
  military_fort: { label: 'Fort', reason: 'the war', difference: 'The muster board: sworn pilots muster for the war cycle; the fort\'s crews waive their labour for them.', cue: 'a briefing hall, the readiness board, the named roster' },
};
/** The registry's role per station key — every registered key is listed (the harness pins all 29). */
export const STATION_ROLES: Readonly<Record<string, StationRole>> = {
  'sys0:st0': 'refinery', 'sys0:st1': 'yard', 'sys0:st2': 'military_fort', 'sys0:st3': 'research_post', 'sys0:st4': 'salvage_yard',
  'sys1:st0': 'trade_hub', 'sys4:st0': 'research_post', 'sys6:st0': 'trade_hub', 'sys8:st0': 'refinery', 'sys10:st0': 'salvage_yard',
  'sys15:st0': 'refinery', 'sys19:st0': 'trade_hub', 'sys26:st0': 'yard', 'sys30:st0': 'salvage_yard',
  'sys37:st0': 'frontier_outpost', 'sys41:st0': 'refinery', 'sys44:st0': 'salvage_yard', 'sys48:st0': 'trade_hub',
  'sys55:st0': 'frontier_outpost', 'sys57:st0': 'frontier_outpost', 'sys59:st0': 'trade_hub', 'sys60:st0': 'yard',
  'sys65:st0': 'refinery', 'sys67:st0': 'frontier_outpost', 'sys70:st0': 'frontier_outpost', 'sys79:st0': 'frontier_outpost',
  'sys86:st0': 'frontier_outpost', 'sys95:st0': 'research_post', 'sys97:st0': 'frontier_outpost',
};
/** A station's role; a key the table does not list (a future Waypoint) is a frontier outpost until it is assigned. */
export function stationRoleOf(key: string): StationRole { return STATION_ROLES[key] ?? 'frontier_outpost'; }

/** Every station of a system: five in the Commons Reach, one Waypoint in a
 *  prebuilt system that hashes a station, none in a user-world system. */
export function stationsOf(sys: StarSystem): StationSpec[] {
  if (sys.id === 'sys0') return [...SYS0_STATIONS];
  if (sys.worldId || !sys.hasStation) return [];
  const st = systemSector(sys).station;
  if (!st) return [];
  return [{ key: `${sys.id}:st0`, systemId: sys.id, name: `${sys.name} Waypoint`, pos: [st[0], st[1], st[2]], radius: ALT_STATION_RADIUS_M, dockRange: ALT_STATION_RADIUS_M + 800, role: stationRoleOf(`${sys.id}:st0`) }];
}

export function stationByKey(systems: readonly StarSystem[], key: string): StationSpec | null {
  if (!STATION_KEY_RE.test(key)) return null;
  const sysId = key.split(':')[0];
  const sys = systems.find((s) => s.id === sysId);
  if (!sys) return null;
  return stationsOf(sys).find((s) => s.key === key) ?? null;
}

/** Where a pilot arrives in a system: 2.4 star radii out on +X, or 2.4 planet
 *  radii off a user world's planet (the client's activate() formula). */
export function systemArrival(sys: StarSystem, wp: { x: number; y: number; z: number; radius: number } | null): [number, number, number] {
  if (sys.worldId && wp) return [wp.x + wp.radius * 2.4, wp.y, wp.z];
  return [systemSector(sys).star.radius * 2.4, 0, 0];
}

/** Bare FNV-1a in [0,1) — SpaceModule's `warH`, the wormhole seat hash. */
export function warH(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 4294967296;
}
/** The box a throat's seat lies in (±x, ±y, ±z metres); the seat itself is SERVER-ONLY
 *  (space_sig — THE HIDDEN-POSITION LAW): a pilot learns it by scanning, never by hashing. */
export const WH_THROAT_BOX_M: readonly [number, number, number] = [130_000, 35_000, 130_000];

/** Ranges the server enforces from the streamed position. */
export const WH_JUMP_RANGE_M = 700;
export const TACKLE_RANGE_M = 6_000;
export const SIEGE_RANGE_M = 8_000;
export const DEPLOY_RANGE_M = 6_000;
/** THE PVP LAW: a blow on another pilot lands only under a live wardec between their guilds,
 *  both in flight in one system, from within this of the target (the streamed positions). */
export const PVP_RANGE_M = 2_500;
export const PVP_HIT_MAX = 12;
export const PVP_HIT_TTL_S = 60;
/** THE CREW LAW: a pilot takes a friend's Sovereign turret seat from within this of the hull,
 *  both in flight in one system; the seat is lost when either docks, jumps or falls silent. */
export const CREW_BOARD_RANGE_M = 3_000;
export const CREW_STALE_S = 120;
/** The dorsal turret's seat above the Sovereign's spine (hull frame, metres). */
export const CREW_TURRET_SEAT: readonly [number, number, number] = [0, 6, -2];
/** The hyper-warp crossing (seconds) and how late a client may complete it
 *  before the sweep does. */
export const SYSJUMP_S = 30;
export const HYPER_GRACE_S = 120;
